import { useQueryClient } from '@tanstack/react-query';
import { insert, remove } from 'ramda';
import { useState, useEffect, useReducer, useCallback, useMemo } from 'react';

import {
  updateCategoryTranslations,
  updateItemTranslations,
  updateOptionBindingTranslations,
  updateOptionTranslations,
  updateOptionValueTranslations,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, MenuContainer, TranslationOperation, Option } from 'apis/restaurant-api/merchant-admin/menu2/types';

import { TranslationSectionItem } from '../types';
import { buildSectionItemKey, findRootElement } from '../utils';

type TranslationPayload = {
  value: string;
  item: TranslationSectionItem;
  pos: 'right' | 'left';
};

type TaskIdentifier = {
  key: string;
  pos: 'right' | 'left';
};

type Action =
  | {
      type: 'add';
      payload: TranslationPayload;
    }
  | { type: 'next' };

const payloadsReducer = (payloads: TranslationPayload[], action: Action) => {
  switch (action.type) {
    case 'add':
      return [...payloads, action.payload];

    case 'next':
      const [, ...next] = payloads;
      return next;

    default:
      return payloads;
  }
};

const useTranslationQueue = (args: {
  menuData: MenuContainer | undefined;
  rightLanguage: Language | null;
  leftLanguage: Language | null;
}) => {
  const { menuData, rightLanguage, leftLanguage } = args;

  const queryClient = useQueryClient();

  const [payloads, dispatch] = useReducer(payloadsReducer, []);

  const [isExecutingTask, setIsExecutingTask] = useState(false);
  const [erroredTasks, setErroredTasks] = useState<TaskIdentifier[]>([]);

  const pendingTasks = useMemo<TaskIdentifier[]>(
    () => payloads.map(({ item, pos }) => ({ key: buildSectionItemKey(item), pos })),
    [payloads],
  );

  const translateItem = useCallback(
    async ({ value, item, pos }: TranslationPayload) => {
      if (!menuData) {
        throw new Error('No menu data available, this should not happen!');
      }
      const { menu, items, options } = menuData;
      const { itemType, itemId, parentId, field } = item;
      const rootElement = findRootElement(itemType, itemId, parentId, menu, items, options);

      if (!rootElement) {
        throw new Error('Described element not found in menu');
      }

      const language = pos === 'right' ? rightLanguage : leftLanguage;

      if (!language) {
        throw new Error('No language selected for column, this should not happen! ');
      }

      const operations: TranslationOperation[] = [
        {
          action: value !== '' ? 'set' : 'unset',
          field,
          language,
          value,
        },
      ];

      switch (itemType) {
        case 'category': {
          const newMenu = await updateCategoryTranslations(menu.id, itemId, menu.version.number, operations);
          return { menu: newMenu, items, options };
        }
        case 'item': {
          const item = rootElement as Item;
          const newItem = await updateItemTranslations(menu.id, item.id, item.version.number, operations);
          const itemIndex = items.findIndex(item => item.id === itemId);
          return { menu, items: insert(itemIndex, newItem, remove(itemIndex, 1, items)), options };
        }
        case 'item_option': {
          const item = rootElement as Item;
          const newItem = await updateOptionBindingTranslations(
            menu.id,
            item.id,
            itemId,
            item.version.number,
            operations,
          );
          const itemIndex = items.findIndex(item => !!item.option_bindings.some(ob => ob.id === itemId));
          return { menu, items: insert(itemIndex, newItem, remove(itemIndex, 1, items)), options };
        }
        case 'option': {
          const option = rootElement as Option;
          const newOption = await updateOptionTranslations(menu.id, option.id, option.version.number, operations);
          const optionIndex = options.findIndex(item => item.id === itemId);
          return { menu, items, options: insert(optionIndex, newOption, remove(optionIndex, 1, options)) };
        }
        case 'option_value': {
          const option = rootElement as Option;
          const newOption = await updateOptionValueTranslations(
            menu.id,
            option.id,
            itemId,
            option.version.number,
            operations,
          );
          const optionIndex = options.findIndex(option => !!option.values.some(choice => choice.id === itemId));
          return { menu, items, options: insert(optionIndex, newOption, remove(optionIndex, 1, options)) };
        }
        default:
          throw new Error(`Unknown type \`${itemType}\``);
      }
    },
    [leftLanguage, menuData, rightLanguage],
  );

  useEffect(() => {
    const func = async () => {
      if (payloads.length > 0 && !isExecutingTask) {
        setIsExecutingTask(true);
        const [payload] = payloads;
        const key = buildSectionItemKey(payload.item);
        try {
          const updatedMenuData = await translateItem(payload);
          queryClient.setQueryData(['read-menu-all', updatedMenuData.menu.id], updatedMenuData);
          setErroredTasks(prev => prev.filter(task => task.key !== key || task.pos !== payload.pos));
        } catch (error) {
          setErroredTasks(prev => [...prev, { key, pos: payload.pos }]);
        } finally {
          dispatch({ type: 'next' });
          setIsExecutingTask(false);
        }
      }
    };

    func();
  }, [payloads, isExecutingTask, translateItem, queryClient]);

  const addTranslationRequest = async (payload: TranslationPayload) => {
    dispatch({ type: 'add', payload });
  };

  return { addTranslationRequest, pendingTasks, erroredTasks };
};

export default useTranslationQueue;
